import { Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigStorage, CrudRowsItemComponent, CrudRowsService, CrudRowsTypeInfo, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { AMMUNITION_STOCK_METADATA, AMMUNITION_STOCK_TYPE, AmmunitionStock, AmmunitionStockEvent, AmmunitionStockEventType, RANGER_METADATA, RANGER_TYPE, Ranger } from 'assets-model';
import { AmmunitionStockService, RangerService } from 'assets-service';


@Directive()
export abstract class AmmunitionStockEventRowsComponentBase<G> extends CrudRowsItemComponent<AmmunitionStockEvent, number, G> implements OnInit {


  defaultSort = null;
  defaultSortDirection = 'asc';
  displayedColumns: string[] = [ 'select', 'ammunitionStockEventId', 'ammunitionStock', 'ranger', 'acknowledgeTokenExpires', 'acknowledged', 'ammunitionStockEventType', 'comments', 'createdBy', 'createdDate', 'dateAcknowledged', 'eventDate', 'quantity', 'actions'];

  protected systemConfig: any;
  
  rowsSelectedByButton: boolean = true;
  
  public form: FormGroup;
  
  ammunitionStockSelectionList: AmmunitionStock[];

  rangerSelectionList: Ranger[];


  constructor(typeInfo: CrudRowsTypeInfo<AmmunitionStockEvent, number, G>,
    protected ammunitionStockEventService: CrudRowsService<AmmunitionStockEvent, number, G>,
    protected ammunitionStockService: AmmunitionStockService,
    protected rangerService: RangerService,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar,
    defaultRowCount?: number) {
    super(typeInfo, fb, elementRef, router, route, configStorage, userStorage, dialog, ammunitionStockEventService, injector, snackbar, defaultRowCount);
  }

  createFormRow(row: AmmunitionStockEvent): FormGroup {
    return this.fb.group({
      ammunitionStockEventId: [row.ammunitionStockEventId, []],
      ammunitionStock: [row.ammunitionStock, []],
      ranger: [row.ranger, []],
      acknowledgeTokenExpires: [row.acknowledgeTokenExpires, []],
      acknowledged: [row.acknowledged, []],
      ammunitionStockEventType: [row.ammunitionStockEventType, [requiredValidator, ]],
      comments: [row.comments, []],
      createdBy: [row.createdBy, []],
      createdDate: [row.createdDate, []],
      dateAcknowledged: [row.dateAcknowledged, []],
      eventDate: [row.eventDate, [requiredValidator, ]],
      quantity: [row.quantity, [requiredValidator, ]]
    });
  }

  createNewItem(): AmmunitionStockEvent {
    return new AmmunitionStockEvent();
  }

  sortingDataAccessor(item: AmmunitionStockEvent, columnDef: string) {
    // important to define these as adding when new rows, nulls MUST be sorted to the bottom
    // or the form controls will break
    switch (columnDef) {
      case 'ammunitionStockEventId':
        if (item?.ammunitionStockEventId == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.ammunitionStockEventId;
        }
      case 'ammunitionStock':
        if (item?.ammunitionStock?.ammunitionStockId == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.ammunitionStock.ammunitionStockId;
        }
      case 'ranger':
        if (item?.ranger?.rangerId == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.ranger.rangerId;
        }
      case 'acknowledgeTokenExpires':
        if (item?.acknowledgeTokenExpires == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.acknowledgeTokenExpires;
        }
      case 'acknowledged':
        if (item?.acknowledged == null) {
          return this.sort.direction == "asc" ? true : false;
        } else {
          return item.acknowledged;
        }
      case 'ammunitionStockEventType':
        if (item?.ammunitionStockEventType == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.ammunitionStockEventType;
        }
      case 'comments':
        if (item?.comments == null) {
          return this.sort.direction == "asc" ? 'Ω' : ' ';
        } else {
          return item.comments;
        }
      case 'createdBy':
        if (item?.createdBy == null) {
          return this.sort.direction == "asc" ? 'Ω' : ' ';
        } else {
          return item.createdBy;
        }
      case 'createdDate':
        if (item?.createdDate == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.createdDate;
        }
      case 'dateAcknowledged':
        if (item?.dateAcknowledged == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.dateAcknowledged;
        }
      case 'eventDate':
        if (item?.eventDate == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.eventDate;
        }
      case 'quantity':
        if (item?.quantity == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.quantity;
        }
      default:
        return item[columnDef];
    }
  }
  
  
  ngOnInit() {
    super.ngOnInit();
    
    function initOptions(config: any): any {
      config = config || {};
      return config;
    }
    this.systemConfig = initOptions({});
    this.configStorage.itemChanges().subscribe(config => {
      this.systemConfig = initOptions(config);
    });
  }
  
  setDefaults() {
    if (!this.readOnly) {

      this.userStorage.nextNonNullItem().subscribe(userDetails => {
      
        // re-trigger updating drop-downs.
        this.rowsSet();
      });
    }
    this.setDefaultDisabledFields();
  }

  rowsSet() {
    super.rowsSet();
    if (this.selectedRowForm()) {
      this.selectedRowForm().markAsPristine();
    }
  }
  
  filtersDisabled() {
    return (this.selectedRow != null && this.isNewRow(this.selectedRow)) || this.selectedForm?.invalid || this.unsavedChanges();
  }


  dataSourceFilter(dataSource: AmmunitionStockEvent[]): AmmunitionStockEvent[] {
    return dataSource;
  }
  
  selectedRowChanged(row: AmmunitionStockEvent): void {
    super.selectedRowChanged(row);
    this.resetSelectionListSubscriptionsForRow(row);
  }

  protected resetSelectionListSubscriptionsForRow(row: AmmunitionStockEvent): void {
  }

  ammunitionStockEventIdRowChanging(value: number, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  ammunitionStockEventIdRowChanged(value: number, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.ammunitionStockEventIdRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  ammunitionStockEventIdRowChangedByUser(value: number, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  ammunitionStockDisplay(item: AmmunitionStock) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return AMMUNITION_STOCK_TYPE.description(item);
  }
  
  ammunitionStockLoadSelectionList() {
    this.ammunitionStockService.findAll().subscribe(
      data => {
        this.ammunitionStockSelectionList = AMMUNITION_STOCK_TYPE.entityDecorator.ensureMultiContained(this.rows && this.rows.map(r => r.ammunitionStock), data.items);
      }
    );
  }

  ammunitionStockRowChanging(value: AmmunitionStock, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  ammunitionStockRowChanged(value: AmmunitionStock, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.ammunitionStockRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  ammunitionStockRowChangedByUser(value: AmmunitionStock, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  ammunitionStockCompare(a: any, b: any) {
    return AMMUNITION_STOCK_METADATA.equals(a, b);
  }
  
  rangerDisplay(item: Ranger) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return RANGER_TYPE.description(item);
  }
  
  rangerLoadSelectionList() {
    this.rangerService.findAll().subscribe(
      data => {
        this.rangerSelectionList = RANGER_TYPE.entityDecorator.ensureMultiContained(this.rows && this.rows.map(r => r.ranger), data.items);
      }
    );
  }

  rangerRowChanging(value: Ranger, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  rangerRowChanged(value: Ranger, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.rangerRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  rangerRowChangedByUser(value: Ranger, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  rangerCompare(a: any, b: any) {
    return RANGER_METADATA.equals(a, b);
  }
  
  acknowledgeTokenExpiresRowChanging(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  acknowledgeTokenExpiresRowChanged(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.acknowledgeTokenExpiresRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  acknowledgeTokenExpiresRowChangedByUser(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  
  ammunitionStockEventTypeRowChanging(value: AmmunitionStockEventType, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  ammunitionStockEventTypeRowChanged(value: AmmunitionStockEventType, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.ammunitionStockEventTypeRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  ammunitionStockEventTypeRowChangedByUser(value: AmmunitionStockEventType, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  commentsRowChanging(value: string, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  commentsRowChanged(value: string, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  commentsRowChangedByUser(value: string, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  createdByRowChanging(value: string, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  createdByRowChanged(value: string, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  createdByRowChangedByUser(value: string, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  createdDateRowChanging(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  createdDateRowChanged(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  createdDateRowChangedByUser(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  dateAcknowledgedRowChanging(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  dateAcknowledgedRowChanged(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.dateAcknowledgedRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  dateAcknowledgedRowChangedByUser(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  eventDateRowChanging(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  eventDateRowChanged(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.eventDateRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  eventDateRowChangedByUser(value: Date, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  
  quantityRowChanging(value: number, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {}
  
  quantityRowChanged(value: number, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.quantityRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  quantityRowChangedByUser(value: number, element: AmmunitionStockEvent, index: number, unsetDependents: boolean = true) {
  }
  


  ammunitionStockEventIdRow(index: number) {
    return this.rowControl('ammunitionStockEventId', index);
  }
  
  ammunitionStockRow(index: number) {
    return this.rowControl('ammunitionStock', index);
  }
  
  rangerRow(index: number) {
    return this.rowControl('ranger', index);
  }
  
  acknowledgeTokenExpiresRow(index: number) {
    return this.rowControl('acknowledgeTokenExpires', index);
  }
  
  acknowledgedRow(index: number) {
    return this.rowControl('acknowledged', index);
  }
  
  ammunitionStockEventTypeRow(index: number) {
    return this.rowControl('ammunitionStockEventType', index);
  }
  
  commentsRow(index: number) {
    return this.rowControl('comments', index);
  }
  
  createdByRow(index: number) {
    return this.rowControl('createdBy', index);
  }
  
  createdDateRow(index: number) {
    return this.rowControl('createdDate', index);
  }
  
  dateAcknowledgedRow(index: number) {
    return this.rowControl('dateAcknowledged', index);
  }
  
  eventDateRow(index: number) {
    return this.rowControl('eventDate', index);
  }
  
  quantityRow(index: number) {
    return this.rowControl('quantity', index);
  }
  
}





